/**
 * TanStack Query integration of `@tcg/realtime` (T14-08): "something changed" → the right queries
 * refetch by themselves (R21), toasts for the changes people must notice, and a yellow banner
 * instead of silently overwriting someone else's edit (`version` / `If-Match` → 409).
 */
import {
  type DefaultError,
  type QueryClient,
  type QueryKey as TanstackQueryKey,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";

import { useRealtime, useRealtimeClient, useRealtimeContext } from "../react";
import type { ChangeMessage } from "../sse";
import type { TopicStatus } from "../types";
import { bridgeFor, type QueryBridge } from "./bridge";
import { createQueryKeyMap, isConventionalKey, type QueryKey } from "./keys";
import { conflictLabels, type ConflictLabels } from "./labels";

export { CONFLICT_LABELS, conflictLabels, type ConflictLabels } from "./labels";
export { createQueryBridge, bridgeFor, type QueryBridge, type QueryBridgeOptions } from "./bridge";
export {
  ENTITY_ROOTS,
  HINT_ROOTS,
  createQueryKeyMap,
  rootOf,
  entityKeys,
  entityOf,
  isConventionalKey,
  type EntityKeys,
  type KeyMap,
  type KeyMapOptions,
  type KeyRule,
  type QueryKey,
} from "./keys";

const defaultKeyMap = createQueryKeyMap();

/** Poll of a live query when realtime is switched off (no client) — design/07 §9.1. */
export const FALLBACK_POLL_MS = 30_000;

/** The bridge of the app's QueryClient (key map / debounce from `<RealtimeProvider>`). */
export function useQueryBridge(): QueryBridge {
  const queryClient = useQueryClient();
  const { keyMap, debounceMs } = useRealtimeContext();
  return bridgeFor(queryClient, {
    keyMap: keyMap ?? defaultKeyMap,
    ...(debounceMs !== undefined ? { debounceMs } : {}),
  });
}

export interface LiveQueryOptions {
  /** Topics whose changes concern this query (`staff:orders`, `public:variant:<id>` …). */
  topics: readonly string[];
  /**
   * Refetch this query for this message? Default: keys that follow the convention
   * (`[root, "list"|"detail", …]`) refetch when the key map names them; any other key refetches
   * on every change of its topics.
   */
  refetchOn?: (message: ChangeMessage) => boolean;
  orderToken?: string | null;
}

/**
 * `useQuery` + subscribe to the topics of this data: a change → the queries it concerns refetch
 * (batched 250 ms); `resync` / the tab coming back / the 30 s fallback poll → this query refetches.
 * Without a realtime client (switched off) it polls every 30 s unless `refetchInterval` is set.
 */
export function useLiveQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends TanstackQueryKey = TanstackQueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & LiveQueryOptions,
): UseQueryResult<TData, TError> {
  const { topics, refetchOn, orderToken, ...queryOptions } = options;
  const bridge = useQueryBridge();
  const client = useRealtimeClient();
  const own: QueryKey = queryOptions.queryKey;
  useRealtime(
    topics,
    (message) => {
      const mapped = bridge.keyMap(message);
      bridge.invalidate(mapped);
      const refetch = refetchOn
        ? refetchOn(message)
        : mapped.length === 0 || !isConventionalKey(own);
      if (refetch) {
        bridge.invalidate([own]);
      }
    },
    {
      enabled: queryOptions.enabled !== false,
      onResync: () => {
        bridge.invalidate([own]);
      },
      orderToken: orderToken ?? null,
    },
  );
  // realtime switched off (no client): poll like the fallback does (design/07 §9.1)
  const result = useQuery({
    ...queryOptions,
    ...(client === null && queryOptions.refetchInterval === undefined && topics.length > 0
      ? { refetchInterval: FALLBACK_POLL_MS }
      : {}),
  } as UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>);
  return result;
}

export interface RealtimeInvalidationOptions {
  enabled?: boolean;
  /**
   * What refetches when the stream missed messages (`resync`, the tab back, the 30 s poll of the
   * fallback): the keys of the screens these topics feed (`[entityKeys("orders").all, …]`), or
   * `"all"`. Required — a default of "every query" refetched every open screen of the app, the
   * record behind an open form included, every 30 s while the hub was down (T14-08 review).
   */
  resyncKeys: readonly QueryKey[] | "all";
}

/**
 * Keep the cache of these topics fresh without a query of their own (e.g. an app shell that
 * listens to `staff:orders` for every order screen). `onResync` → `resyncKeys`.
 */
export function useRealtimeInvalidation(
  topics: readonly string[],
  options: RealtimeInvalidationOptions,
): TopicStatus {
  const bridge = useQueryBridge();
  return useRealtime(
    topics,
    (message) => {
      bridge.handle(message);
    },
    {
      enabled: options.enabled ?? true,
      onResync: () => {
        const keys = options.resyncKeys;
        if (keys === "all") {
          bridge.invalidate([]);
        } else if (keys.length > 0) {
          bridge.invalidate(keys);
        }
      },
    },
  );
}

// ---- toasts -------------------------------------------------------------------------------------

export interface ToastRule<T> {
  entity?: string;
  /** One action or several (`created`, `paid`, `availability_changed` …). */
  action?: string | readonly string[];
  /** Topic, or a prefix ending with `:` (`pos:register:`). */
  topic?: string;
  when?: (message: ChangeMessage) => boolean;
  toast: (message: ChangeMessage) => T | null;
}

export interface RealtimeToastOptions {
  enabled?: boolean;
  /** Messages older than this (replayed after a reconnect) do not toast. Default 2 min. */
  maxAgeMs?: number;
}

function matches<T>(rule: ToastRule<T>, message: ChangeMessage): boolean {
  if (rule.entity !== undefined && rule.entity !== message.entity) {
    return false;
  }
  if (rule.action !== undefined) {
    const actions: readonly string[] =
      typeof rule.action === "string" ? [rule.action] : rule.action;
    if (!actions.includes(message.action)) {
      return false;
    }
  }
  if (rule.topic !== undefined) {
    const ok = rule.topic.endsWith(":")
      ? message.topic.startsWith(rule.topic)
      : message.topic === rule.topic;
    if (!ok) {
      return false;
    }
  }
  return rule.when?.(message) ?? true;
}

/**
 * Toasts for the changes people must notice (new order, transfer received, card sold …) — each app
 * chooses its rules and shows `T` with its own toast component. The same change seen twice (two
 * topics, a replay) toasts once.
 */
export function useRealtimeToasts<T>(
  topics: readonly string[],
  rules: ReadonlyArray<ToastRule<T>>,
  show: (toast: T, message: ChangeMessage) => void,
  options: RealtimeToastOptions = {},
): TopicStatus {
  const seen = useRef(new Map<string, number>());
  const maxAgeMs = options.maxAgeMs ?? 120_000;
  return useRealtime(
    topics,
    (message) => {
      const now = Date.now();
      const at = Date.parse(message.at);
      if (Number.isFinite(at) && now - at > maxAgeMs) {
        return;
      }
      const key = `${message.entity ?? message.topic}|${message.id}|${message.action}|${String(message.version)}`;
      for (const [k, t] of seen.current) {
        if (now - t > maxAgeMs) {
          seen.current.delete(k);
        }
      }
      if (seen.current.has(key)) {
        return;
      }
      for (const rule of rules) {
        if (matches(rule, message)) {
          const toast = rule.toast(message);
          if (toast !== null) {
            seen.current.set(key, now);
            show(toast, message);
            return;
          }
        }
      }
    },
    { enabled: options.enabled ?? true },
  );
}

// ---- conflicts (version / If-Match) --------------------------------------------------------------

export interface ConflictGuardOptions {
  /** Topics that carry changes of this record (`staff:catalog` …). */
  topics: readonly string[];
  entity: string;
  id: string | null | undefined;
  /** Version the form was opened with. */
  version: number | null | undefined;
  /** The form has unsaved changes. */
  editing: boolean;
  /** A save is running (its own change comes back as a message — not a conflict). */
  saving?: boolean;
  /** Query to refetch on "Load latest". */
  queryKey?: QueryKey;
  /** After "Load latest" refetched: reset the form to the new data. */
  onReload?: () => void;
  /**
   * Version our own save returned (the mutation's response): its echo is ours even when it arrives
   * after `saving` ended but before `version` (the form's record) was refetched.
   */
  savedVersion?: number | null;
  /**
   * Right after our own save (`saving` → false): a message without `version`, or with the version
   * just after the one the form was opened with, is our own echo. Default 3 s.
   */
  ownEchoMs?: number;
}

export interface ConflictState {
  conflict: boolean;
  /** Version someone else saved (null when the producer sends none). */
  latestVersion: number | null;
  at: string | null;
  /** Refetch + reset the form (via `onReload`), then hide the banner. */
  reload: () => void;
  dismiss: () => void;
}

/**
 * Watches the record being edited: another person's save (newer `version`) while the form has
 * unsaved changes → `conflict` (show {@link ConflictBanner}); nothing is overwritten silently.
 * Without unsaved changes the live query simply refreshes the form.
 */
export function useConflictGuard(options: ConflictGuardOptions): ConflictState {
  const queryClient = useQueryClient();
  // the conflict belongs to the record + version it was seen for: a reload / own save (new
  // version) or another record hides it without an effect
  const [state, setState] = useState<{
    forId: string;
    forVersion: number | null;
    version: number | null;
    at: string;
  } | null>(null);
  const latest = useRef(options);
  const savedAt = useRef(0);
  const wasSaving = useRef(false);
  useEffect(() => {
    latest.current = options;
  });
  useEffect(() => {
    if (wasSaving.current && !options.saving) {
      savedAt.current = Date.now();
    }
    wasSaving.current = options.saving ?? false;
  }, [options.saving]);

  useRealtime(
    options.topics,
    (message) => {
      const { id, entity, editing, saving, version, savedVersion, ownEchoMs } = latest.current;
      if (!id || message.entity !== entity || message.id !== id || !editing || saving) {
        return;
      }
      const seen = Math.max(
        typeof version === "number" ? version : -1,
        typeof savedVersion === "number" ? savedVersion : -1,
      );
      if (message.version !== null && message.version <= seen) {
        return; // our own save / an older change
      }
      const justSaved = Date.now() - savedAt.current < (ownEchoMs ?? 3_000);
      if (
        justSaved &&
        (message.version === null ||
          (typeof version === "number" && message.version === version + 1))
      ) {
        return; // the echo of the save that just ended (the form's version is not refetched yet)
      }
      setState({
        forId: id,
        forVersion: version ?? null,
        version: message.version,
        at: message.at,
      });
    },
    { enabled: Boolean(options.id) },
  );

  const current =
    state !== null && state.forId === options.id && state.forVersion === (options.version ?? null)
      ? state
      : null;
  return useMemo(
    () => ({
      conflict: current !== null,
      latestVersion: current?.version ?? null,
      at: current?.at ?? null,
      reload: () => {
        const o = latest.current;
        setState(null);
        const done = o.queryKey
          ? queryClient.invalidateQueries({ queryKey: [...o.queryKey] })
          : Promise.resolve();
        void done.then(() => o.onReload?.());
      },
      dismiss: () => {
        setState(null);
      },
    }),
    [current, queryClient],
  );
}

export interface ConflictBannerProps {
  /** Show it (`useConflictGuard().conflict`). */
  show: boolean;
  onReload: () => void;
  /** Who saved, when the screen knows it (from the refetched record — never from the message). */
  by?: string | null;
  locale?: string;
  labels?: Partial<ConflictLabels>;
  className?: string;
  style?: CSSProperties;
}

/** Yellow bar "Someone else changed this — [Load latest]" (design/04 §0 plain words). */
export function ConflictBanner({
  show,
  onReload,
  by,
  locale,
  labels,
  className,
  style,
}: ConflictBannerProps) {
  const context = useRealtimeContext();
  if (!show) {
    return null;
  }
  const words = conflictLabels(locale ?? context.locale, labels);
  const text = by ? words.conflictBy.replace("{name}", by) : words.conflict;
  return (
    <div
      role="alert"
      data-realtime-conflict=""
      className={className}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.625rem 0.875rem",
        background: "#fef3c7",
        borderLeft: "4px solid var(--color-status-pending, #f59e0b)",
        color: "#111",
        ...style,
      }}
    >
      <span style={{ flex: "1 1 16rem" }}>{text}</span>
      <button
        type="button"
        onClick={onReload}
        style={{
          minHeight: "44px",
          padding: "0 1rem",
          border: "1px solid currentColor",
          borderRadius: "4px",
          background: "#fff",
          cursor: "pointer",
          font: "inherit",
          fontWeight: 600,
        }}
      >
        {words.reload}
      </button>
    </div>
  );
}

/** `If-Match` header for a save of the version the form was opened with (`{}` when unknown). */
export function ifMatch(version: number | null | undefined): { "If-Match"?: string } {
  return version === null || version === undefined ? {} : { "If-Match": `"${String(version)}"` };
}

/**
 * `true` for a `version_conflict` error (error envelope, openapi-fetch `error`, a parsed body).
 * Only the code counts: other 409s ("card already sold", "order under review" …) are not "someone
 * else saved first" — the shared error envelope always carries a code (T14-08 review).
 */
export function isVersionConflict(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const value = error as {
    code?: unknown;
    error?: { code?: unknown };
    body?: { error?: { code?: unknown } };
  };
  return (
    value.code === "version_conflict" ||
    value.error?.code === "version_conflict" ||
    value.body?.error?.code === "version_conflict"
  );
}

/**
 * After a save failed: a version conflict → refetch the record (the screen shows the latest data)
 * and return `true` (show `labels.savedByOther`); anything else → `false`.
 */
export function handleVersionConflict(
  error: unknown,
  queryClient: QueryClient,
  queryKey: QueryKey,
): boolean {
  if (!isVersionConflict(error)) {
    return false;
  }
  void queryClient.invalidateQueries({ queryKey: [...queryKey] });
  return true;
}
